import { flexRender, Row } from "@tanstack/react-table";

import {
    Autocomplete,
    Spinner,
    Table,
    TableBody,
    TableCell,
    TableColumn,
    TableHeader,
    TableProps,
    TableRow,
} from "@heroui/react";
import { Table as TanstackTable } from "@tanstack/react-table";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowDownShortWide,
    faArrowUpShortWide,
} from "@fortawesome/free-solid-svg-icons";

function CommonTable({
    label,
    table,
    height,
    filterOptions,
    isLoading,
    tableProps,
}: {
    label: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    table: TanstackTable<any>;
    height: number;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    filterOptions?: Record<string, any>;
    isLoading: boolean;
    tableProps?: TableProps;
}) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rows = table.getRowModel().rows as Row<any>[];
    return (
        <Table
            aria-label={label}
            fullWidth
            classNames={{
                th: "bg-transparent p-0 px-1 align-top",
                thead: "border-b border-gray-200 items-start justify-items-start",
                wrapper: "p-0 shadow-none rounded-none",
                tr: "hover:cursor-pointer",
            }}
            className="h-full"
            isStriped
            isVirtualized
            style={{
                height,
            }}
            maxTableHeight={height}
            rowHeight={30}
            {...tableProps}
        >
            <TableHeader>
                {table
                    .getHeaderGroups()
                    .map((group) => {
                        return group.headers.map((header) => {
                            return (
                                <TableColumn
                                    aria-label={
                                        header.column.columnDef.header?.toString() ??
                                        "column header"
                                    }
                                    key={header.id}
                                    style={{
                                        width: header.column.getSize(),
                                    }}
                                    className="pointer-events-none"
                                >
                                    <div
                                        onClick={header.column.getToggleSortingHandler()}
                                        style={{
                                            width: header.column.getSize(),
                                        }}
                                        className={classNames({
                                            "hover:bg-gray-200 rounded-lg p-2 text-medium pointer-events-auto mb-1":
                                                true,
                                            "cursor-pointer":
                                                header.column.getCanSort(),
                                        })}
                                    >
                                        {flexRender(
                                            header.column.columnDef.header,
                                            header.getContext(),
                                        )}
                                        {header.column.getCanSort() &&
                                            header.column.getIsSorted() && (
                                                <FontAwesomeIcon
                                                    className="ml-2 text-primary-500"
                                                    icon={
                                                        header.column.getIsSorted() ===
                                                        "asc"
                                                            ? faArrowUpShortWide
                                                            : faArrowDownShortWide
                                                    }
                                                    size="sm"
                                                />
                                            )}
                                    </div>

                                    {header.column.getCanFilter() &&
                                        header.column.id in
                                            (filterOptions ?? {}) && (
                                            <div tabIndex={-1}>
                                                <Autocomplete
                                                    color={
                                                        header.column.getFilterValue()
                                                            ? "primary"
                                                            : "default"
                                                    }
                                                    aria-label="Filter"
                                                    className="w-full pointer-events-auto"
                                                    size="sm"
                                                    showScrollIndicators={false}
                                                    selectedKey={
                                                        header.column.getFilterValue() as string
                                                    }
                                                    onSelectionChange={(e) => {
                                                        header.column.setFilterValue(
                                                            e,
                                                        );
                                                    }}
                                                >
                                                    {
                                                        (filterOptions ?? {})[
                                                            header.column
                                                                .id as keyof typeof filterOptions
                                                        ]
                                                    }
                                                </Autocomplete>
                                            </div>
                                        )}
                                </TableColumn>
                            );
                        });
                    })
                    .flat()}
            </TableHeader>
            <TableBody
                isLoading={isLoading}
                loadingContent={<Spinner label="Loading..." />}
                emptyContent={
                    <div className="flex justify-center items-center h-full">
                        <p className="text-gray-500">
                            No data found matching your filters
                        </p>
                    </div>
                }
                items={rows}
            >
                {(row) => {
                    return (
                        <TableRow key={row.original.id}>
                            {row.getVisibleCells().map((cell) => (
                                <TableCell
                                    key={cell.id}
                                    style={{
                                        width: cell.column.getSize(),
                                    }}
                                    aria-label={
                                        cell
                                            .getContext()
                                            .getValue()
                                            ?.toString() ?? "cell-value"
                                    }
                                >
                                    {flexRender(
                                        cell.column.columnDef.cell,
                                        cell.getContext(),
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    );
                }}
            </TableBody>
        </Table>
    );
}

export default CommonTable;
